import { useCallback } from "react";

import { useApolloClient, useMutation } from "@apollo/client";
import { useIntl } from "react-intl";

import { useSelectedOrganizationContainer } from "../../shared/hooks";
import { bulkPatchRemoteAssetsMutation } from "../api/mutations/bulkPatchRemoteAssets";
import {
    type BulkPatchRemoteAssetsMutation,
    type BulkPatchRemoteAssetsMutationVariables,
} from "../api/mutations/bulkPatchRemoteAssets.gql-generated";

import { messages } from "./messages.generated";
import { formatApolloError } from "./useErrorMessage";
import { type SetMutationStatus } from "./useMutationStatus";

interface BulkUpdateForkingResult {
    bulkUpdateForking: (changes: { enableIds: string[]; disableIds: string[] }) => Promise<boolean>;
}

export const useBulkUpdateForking = (setStatus: SetMutationStatus): BulkUpdateForkingResult => {
    const { orgId } = useSelectedOrganizationContainer();
    const intl = useIntl();
    const client = useApolloClient();

    const [patchMutation] = useMutation<BulkPatchRemoteAssetsMutation, BulkPatchRemoteAssetsMutationVariables>(
        bulkPatchRemoteAssetsMutation,
    );

    const bulkUpdateForking = useCallback(
        async (changes: { enableIds: string[]; disableIds: string[] }) => {
            if (!orgId) {
                setStatus({
                    type: "error",
                    action: "bulkUpdateForking",
                    message: intl.formatMessage(messages.noOrganizationSelectedError),
                });
                return false;
            }

            setStatus({ type: "pending", action: "bulkUpdateForking" });
            try {
                const promises: ReturnType<typeof patchMutation>[] = [];

                if (changes.enableIds.length > 0) {
                    promises.push(
                        patchMutation({
                            variables: {
                                organizationId: orgId,
                                assetIds: changes.enableIds,
                                forkingAllowed: true,
                            },
                        }),
                    );
                }

                if (changes.disableIds.length > 0) {
                    promises.push(
                        patchMutation({
                            variables: {
                                organizationId: orgId,
                                assetIds: changes.disableIds,
                                forkingAllowed: false,
                            },
                        }),
                    );
                }

                const results = await Promise.all(promises);

                const failedItems = results.flatMap(
                    (result) => result.data?.bulkPatchRemoteAssets.results.filter((r) => r.error !== null) ?? [],
                );
                const failedIds = new Set(failedItems.map((r) => r.assetId));

                // Write updated forkingAllowed directly to cache.
                // NOTE: The mutation response only returns assetId + error,
                // not the full asset. So updatedAt/updatedBy remain stale
                // until the next network fetch. A backend change to return
                // the full asset fragment would eliminate this staleness.
                for (const id of changes.enableIds) {
                    if (!failedIds.has(id)) {
                        client.cache.modify({
                            id: client.cache.identify({ __typename: "RemoteAsset", id }),
                            fields: { forkingAllowed: () => true },
                        });
                    }
                }
                for (const id of changes.disableIds) {
                    if (!failedIds.has(id)) {
                        client.cache.modify({
                            id: client.cache.identify({ __typename: "RemoteAsset", id }),
                            fields: { forkingAllowed: () => false },
                        });
                    }
                }

                if (failedItems.length > 0) {
                    setStatus({
                        type: "error",
                        action: "bulkUpdateForking",
                        message: intl.formatMessage(messages.bulkUpdateForkingPartialError, {
                            failedCount: failedItems.length,
                        }),
                    });
                    return false;
                } else {
                    setStatus({ type: "success", action: "bulkUpdateForking" });
                    return true;
                }
            } catch (error) {
                console.error("Failed to bulk update forking:", error);
                setStatus({
                    type: "error",
                    action: "bulkUpdateForking",
                    message: formatApolloError(error, intl),
                });
                return false;
            }
        },
        [client, orgId, patchMutation, setStatus, intl],
    );

    return { bulkUpdateForking };
};
